import { useState, useEffect, useRef } from "react";
import { ArrowRight } from "lucide-react";
import GuaranteedRentClosedModal from "./GuaranteedRentClosedModal";

function attachAutoComplete(input, onSelect, country) {
  if (!input) return;
  const init = () => {
    if (!window.google?.maps?.places) return false;
    try {
      const ac = new window.google.maps.places.Autocomplete(input, {
        types: ["address"],
        componentRestrictions: { country },
        fields: ["formatted_address"],
      });
      ac.addListener("place_changed", () => {
        const place = ac.getPlace();
        if (place?.formatted_address) onSelect(place.formatted_address);
      });
      input.addEventListener("keydown", e => { if (e.key === "Enter") e.preventDefault(); });
      return true;
    } catch { return false; }
  };
  if (!init()) {
    const iv = setInterval(() => { if (init()) clearInterval(iv); }, 200);
    setTimeout(() => clearInterval(iv), 15000);
  }
}

export default function GuaranteedRentHero({ city }) {
  const [address, setAddress] = useState("");
  const [showModal, setShowModal] = useState(false);
  const addressRef = useRef(null);

  useEffect(() => {
    attachAutoComplete(addressRef.current, (addr) => setAddress(addr), city.country);
  }, [city.country]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowModal(true);
  };

  return (
    <section style={{
      position: "relative",
      backgroundColor: "#fff",
      paddingTop: "clamp(130px, 15vw, 180px)",
      paddingBottom: "clamp(60px, 7vw, 80px)",
      paddingLeft: "clamp(20px, 5vw, 80px)",
      paddingRight: "clamp(20px, 5vw, 80px)",
    }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "clamp(40px, 6vw, 80px)", alignItems: "start" }} className="gr-hero-grid">

          <div>
            <div style={{ display: "inline-flex", alignItems: "center", background: "#F4F1E7", border: "1px solid #E8E4D5", borderRadius: 100, padding: "6px 14px", marginBottom: 24 }}>
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 600, color: "#CD533B", letterSpacing: "0.06em", textTransform: "uppercase" }}>Guaranteed Rent{city.name ? " " + city.name : ""}</span>
            </div>

            <h1 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: "clamp(38px, 5vw, 64px)", lineHeight: 1.1, letterSpacing: "-0.025em", color: "#112215", margin: "0 0 24px" }}>
              Fixed Monthly Income.<br />
              <span style={{ color: "#CD533B" }}>Zero Voids.</span>
            </h1>

            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "clamp(15px, 1.6vw, 18px)", color: "#5C7268", lineHeight: 1.65, margin: "0 0 32px", maxWidth: 480 }}>
              We take your{city.name ? " " + city.name : ""} property on a multi-year contract, operate it as serviced accommodation, and pay you the same fixed amount every month - whether it's occupied or not. No voids, no arrears, no hassle.
            </p>

            <form
              id="main-form"
              onSubmit={handleSubmit}
              style={{ display: "flex", alignItems: "center", background: "#fff", border: "1.5px solid #D5D0BE", borderRadius: 10, padding: "6px 6px 6px 20px", boxShadow: "0 4px 24px rgba(0,0,0,0.07)", maxWidth: 480, width: "100%" }}
            >
              <input
                ref={addressRef}
                type="text"
                value={address}
                onChange={e => setAddress(e.target.value)}
                placeholder="Enter your property address"
                autoComplete="off"
                style={{ flex: 1, border: "none", outline: "none", fontFamily: "'Inter', sans-serif", fontSize: 15, color: "#112215", background: "transparent", minWidth: 0 }}
              />
              <button
                type="submit"
                style={{ background: "#112215", color: "#fff", border: "none", borderRadius: 7, padding: "12px 16px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "background 0.2s", gap: 8 }}
                onMouseEnter={e => e.currentTarget.style.background = "#1A3A2A"}
                onMouseLeave={e => e.currentTarget.style.background = "#112215"}
                aria-label="Get guaranteed rent estimate"
              >
                <span className="btn-text" style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 600 }}>Get Estimate</span>
                <ArrowRight size={18} />
              </button>
            </form>
          </div>

          <div style={{ position: "relative", height: 480 }} className="gr-hero-images">
            <div style={{
              position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
              borderRadius: 16, overflow: "hidden",
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            }}>
              <img src={city.heroImage} alt={city.name ? `${city.name} cityscape - Truestays guaranteed rent ${city.name}` : "Truestays guaranteed rent"} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} fetchpriority="high" decoding="async" width="800" height="480" />
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <GuaranteedRentClosedModal baseForm={{ address }} onClose={() => setShowModal(false)} />
      )}

      <style>{`
        @media (max-width: 768px) {
          .gr-hero-grid { grid-template-columns: 1fr !important; }
          .gr-hero-images { display: block !important; margin-top: 32px; height: 320px !important; }
          .btn-text { display: none !important; }
        }
        @media (max-width: 480px) {
          section { padding-left: 16px !important; padding-right: 16px !important; }
          h1 { font-size: 28px !important; line-height: 1.2 !important; }
          p { font-size: 14px !important; }
        }
      `}</style>
    </section>
  );
}